import { useEffect, useMemo, useRef, useState } from "react";
import {
    DistributionData,
    GeoDetails,
    GeoTag,
    MetricMetadata,
    RawIndicatorData,
} from "../services/types";
import {
    fetchDistributionData,
    fetchIndicatorBinCount,
    fetchMetricMetadata,
} from "../utils/dashboardUtils";

interface UseMetricMetadataArgs {
    metricId?: number;
    geoDetails?: GeoDetails[];
    geoTag?: GeoTag | null;
    enabled?: boolean;
}

export const useMetricMetadata = ({
    metricId,
    geoDetails = [],
    geoTag = null,
    enabled = true,
}: UseMetricMetadataArgs) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const clickDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

    const modalId = useMemo(
        () => `metric-metadata-modal-${metricId ?? "main"}`,
        [metricId]
    );

    const geoKey = useMemo(
        () => geoDetails.map((d) => d.lod).join(","),
        [geoDetails]
    );

    function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
        if (fetchCache.current[key]) {
        return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
        delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    useEffect(() => {
        if (!enabled || !metricId) return;

        if (!metadata) {
        fetchWithCache(`metadata-${metricId}`, () => fetchMetricMetadata(metricId))
            .then((res) => res?.data && setMetadata(res.data))
            .catch(console.error);
        }

        if (!distributionData) {
        const distKey = `distData-${metricId}-${geoKey}-${geoTag?.type}-${geoTag?.value}`;
        fetchWithCache(distKey, () => fetchIndicatorBinCount(metricId, geoDetails, geoTag))
            .then((res) => res && setDistributionData(res))
            .catch(console.error);
        }

        if (!countyDistributionData) {
        const countyKey = `countyDist-${metricId}-53033`;
        fetchWithCache(countyKey, () => fetchDistributionData(metricId, [], null))
            .then((res) => res && setCountyDistributionData(res))
            .catch(console.error);
        }
    }, [
        enabled,
        metricId,
        metadata,
        distributionData,
        countyDistributionData,
        geoDetails,
        geoKey,
        geoTag,
    ]);

    const toggleMetadata = async () => {
        if (!metricId) return;

        if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);

        clickDebounceRef.current = setTimeout(async () => {
        setShowMetadata((prev) => !prev);
        setLoading(true);

        try {
            const distKey = `distData-${metricId}-${geoKey}-${geoTag?.type}-${geoTag?.value}`;
            const countyKey = `countyDist-${metricId}-53033`;

            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] =
            await Promise.all([
                metadata
                ? Promise.resolve({ data: metadata })
                : fetchWithCache(`metadata-${metricId}`, () => fetchMetricMetadata(metricId)),

                distributionData
                ? Promise.resolve(distributionData)
                : fetchWithCache(distKey, () =>
                    fetchIndicatorBinCount(metricId, geoDetails, geoTag)
                    ),

                countyDistributionData
                ? Promise.resolve(countyDistributionData)
                : fetchWithCache(countyKey, () =>
                    fetchDistributionData(metricId, [], null)
                    ),
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) {
            setCountyDistributionData(countyDistributionDataResponse || null);
            }
        } catch (error) {
            const errorMessage =
            (error as Error).message || "An unexpected error occurred.";
            console.error("Error fetching data:", error);
            setError(new Error(`Failed to load data: ${errorMessage}`));
        } finally {
            setLoading(false);
        }
        }, 150);
    };

    return {
        showMetadata,
        toggleMetadata,
        metadata,
        distributionData,
        countyDistributionData,
        loading,
        error,
        triggerRef,
        modalId,
    };
};